import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { API_BASE } from '../utils/apiEndpoints';
import { fetchEditorSettings } from '../utils/sqlEditorSettingsApi.ts';
import {
  writeStoredCodeCustomThemeTokens,
  writeStoredCodeEditorBackgroundMode,
  writeStoredCodeSyntaxTheme,
  writeStoredSqlCustomThemeTokens,
  writeStoredSqlEditorBackgroundMode,
  writeStoredSqlSyntaxTheme,
} from '../utils/monacoThemes.ts';

interface User {
  id: string;
  username: string;
  email?: string;
  avatar?: string;
  role?: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (userData: User) => void;
  logout: () => void;
  updateUser: (updates: Partial<User>) => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUserData = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/profile/profile`, {
        credentials: 'include'
      });
      if (response.ok) {
        const p = await response.json();
        return { id: String(p.id), username: p.username, email: p.email, avatar: p.avatar, role: p.role || 'user' };
      }
      return null;
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      return null;
    }
  };

  const hydrateEditorSettingsCache = async () => {
    const editorSettings = await fetchEditorSettings();
    if (!editorSettings) return;
    if (editorSettings.sqlSyntaxTheme) {
      writeStoredSqlSyntaxTheme(editorSettings.sqlSyntaxTheme);
    }
    writeStoredSqlCustomThemeTokens(editorSettings.sqlCustomThemeTokens);
    if (editorSettings.codeSyntaxTheme) {
      writeStoredCodeSyntaxTheme(editorSettings.codeSyntaxTheme);
    }
    writeStoredCodeCustomThemeTokens(editorSettings.codeCustomThemeTokens);
    writeStoredSqlEditorBackgroundMode(editorSettings.sqlEditorBackgroundMode);
    writeStoredCodeEditorBackgroundMode(editorSettings.codeEditorBackgroundMode);
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const userData = await fetchUserData();
        if (userData) {
          setUser(userData);
          await hydrateEditorSettingsCache();
        }
      } catch (error) {
        console.error('Auth check failed:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = (userData: User) => {
    setUser(userData);
    void hydrateEditorSettingsCache();
  };

  const logout = async () => {
    try {
      await fetch(`${API_BASE}/api/login/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (e) {
      console.error('Logout request failed:', e);
    } finally {
      setUser(null);
    }
  };

  const updateUser = (updates: Partial<User>) => {
    setUser(prev => prev ? { ...prev, ...updates } : null);
  };

  const value = {
    isAuthenticated: !!user,
    user,
    login,
    logout,
    updateUser,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
